import { fetchEndpointData } from '@utils/fetchEndpointData';
import useSWR from 'swr';
import type { IPdfJobRef } from 'src/queue/pdf-job-queues';

import type {
  CargoTypesResponse,
  CreateCargoTypeBody,
  UpdateCargoTypeBody,
  CargoTypeIdResponse,
  PackageTypesResponse,
  CreatePackageTypeBody,
  UpdatePackageTypeBody,
  PackageTypeIdResponse,
  ProductTypesResponse,
  CreateProductTypeBody,
  UpdateProductTypeBody,
  ProductTypeIdResponse,
  VatRatesResponse,
  CreateVatRateBody,
  UpdateVatRateBody,
  VatRateIdResponse,
} from './endpoints/refdata/ReferenceDataEndpoints.types';
import type { OrderLocationsQuery, OrderLocationsResponse } from './endpoints/OrderLocationsEndpoint';
import type { OrdersOrderQuery, OrdersResponse } from './endpoints/OrdersEndpoint';
import type { OrdersFilterQuery } from './endpoints/utils';
import type { OrdersExportTriggerBody, OrdersExportTriggerResponse } from './endpoints/OrdersExportTriggerEndpoint';
import type { OrdersExportPingProgressResponse } from './endpoints/OrdersExportPingProgressEndpoint';
import type { OrderTrackingEvent, OrderTrackingEventsResponse } from './endpoints/OrderTrackingEventsEndpoint.types';
import type { OrderTruckTrackingResponse } from './endpoints/OrderTruckTrackingEndpoint.types';
import type { OrderTrailerTrackingResponse } from './endpoints/OrderTrailerTrackingEndpoint.types';
import type { OrderRouteDataResponse, CalculateOrderRouteResponse } from '../tracking/endpoints/types';
import type { OrderAssignBody, OrderAssignResponse } from './endpoints/OrderAssignEndpoint.types';
import type { OrderSelfBillExclusionBody } from './endpoints/OrderSelfBillExclusionEndpoint.types';
import type {
  OrderChatMessage,
  OrderChatMessagesResponse,
  OrderChatSendResponse,
} from './endpoints/chat/OrderChatEndpoint.types';

// --- GraphQL -> REST order core migration (tra-93) ---
import type {
  GetOrdersFilter,
  GetOrdersOrder,
  GetOrdersOrderBy,
  GetOrdersResponse,
} from './endpoints/GetOrdersEndpoint.types';
import type { GetOrderByIdResponse } from './endpoints/GetOrderByIdEndpoint.types';
import type {
  IncomingOrderListItem,
  IncomingOrderResponse,
  IncomingOrdersResponse,
} from './endpoints/IncomingOrderEndpoints.types';
import type { HistoricalOrderLinesResponse, OrdersByTokenResponse } from './endpoints/OrdersByTokenEndpoints.types';
import type {
  BulkDuplicateOrderBody,
  BulkDuplicateOrderResponse,
  ChangeOrderSaleStatusBody,
  ConfirmOrderByTokenBody,
  ConfirmOrderResponse,
  CreateOrderBody,
  OrderIdResponse,
  SetOrderDocumentValidationStatusBody,
  SetOrderDocumentValidationStatusResponse,
  UpdateOrderBody,
  UpdateOrderStatusBody,
} from './endpoints/OrderMutationEndpoints.types';
import type {
  CreateOrderLineBody,
  CreateOrderLineContentBody,
  CreateOrderLinePurchaseBody,
  CreateOrderLineSaleBody,
  CreateOrderLineStopBody,
  NumericIdResponse,
  StringIdResponse,
  UpdateOrderLineContentBody,
  UpdateOrderLinePurchaseBody,
  UpdateOrderLineSaleBody,
  UpdateOrderLineStopBody,
} from './endpoints/OrderLineEndpoints.types';
import type {
  ComplaintAttachmentsResponse,
  InternalLogOrderComplaintBody,
  LinkOrderDocumentBody,
  UnlinkOrderDocumentBody,
  UpdateOrderComplaintBody,
  UpdateOrderComplaintResponse,
} from './endpoints/OrderDocumentComplaintEndpoints.types';
import type {
  SendOrderConfirmationBody,
  SendTransportOrderBody,
  SendUrgentCmrReminderBody,
} from './endpoints/OrderPdfSendEndpoints.types';

export async function fetchOrderLocations(query: OrderLocationsQuery) {
  const searchParams = new URLSearchParams();
  if (query.type) {
    searchParams.append('type', query.type);
  }

  const response = await fetchEndpointData<OrderLocationsResponse>(
    `/api/orders/order-locations?${searchParams.toString()}`,
    {
      method: 'GET',
    },
  );
  return response;
}

export interface IFetchOrdersQuery {
  id?: string;
  take?: number;
  filters?: OrdersFilterQuery;
  orderBy?: OrdersOrderQuery;
}

export async function fetchMinimalOrders(query: IFetchOrdersQuery) {
  const searchParams = new URLSearchParams();
  if (query.id) {
    searchParams.append('id', query.id);
  }
  if (query.take) {
    searchParams.append('take', query.take.toString());
  }
  if (query.filters) {
    searchParams.append('filters', JSON.stringify(query.filters));
  }
  if (query.orderBy) {
    searchParams.append('orderBy', JSON.stringify(query.orderBy));
  }

  const response = await fetchEndpointData<OrdersResponse>(
    `/api/orders/internal-minimal-list?${searchParams.toString()}`,
    {
      method: 'GET',
    },
  );
  return response;
}

export async function startOrdersExport(body: OrdersExportTriggerBody) {
  const response = await fetchEndpointData<OrdersExportTriggerResponse>(`/api/orders/orders-export`, {
    method: 'POST',
    body,
  });
  return response;
}

export async function ordersExportStatus(jobId: string) {
  const queryString = new URLSearchParams();
  queryString.append('jobId', jobId);
  const response = await fetchEndpointData<OrdersExportPingProgressResponse>(
    `/api/orders/orders-export?${queryString.toString()}`,
    {
      method: 'GET',
    },
  );
  return response;
}

export async function getOrderTrackingEvents(orderId: string): Promise<OrderTrackingEvent[]> {
  const response = await fetchEndpointData<OrderTrackingEventsResponse>(`/api/v1/orders/${orderId}/tracking-events`);
  return response.events;
}

export function useOrderTrackingEvents(orderId: string) {
  return useSWR(['order-tracking-events', orderId], () => getOrderTrackingEvents(orderId), {
    refreshInterval: 60000,
  });
}

export async function getOrderTruckTracking(orderId: string): Promise<OrderTruckTrackingResponse> {
  return fetchEndpointData<OrderTruckTrackingResponse>(`/api/v1/orders/${orderId}/truck-tracking`);
}

/**
 * Where the order's truck is according to its tracking connection.
 *
 * Deliberately not polled, unlike the tracking events above: the snapshot is refreshed from the provider
 * when the page loads, and an order page left open must not keep spending the carrier's API quota. Focus and
 * reconnect revalidation are off for that same reason — SWR's defaults would re-ask on every tab switch.
 */
export function useOrderTruckTracking(orderId: string) {
  return useSWR(['order-truck-tracking', orderId], () => getOrderTruckTracking(orderId), {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });
}

export async function getOrderTrailerTracking(orderId: string): Promise<OrderTrailerTrackingResponse> {
  return fetchEndpointData<OrderTrailerTrackingResponse>(`/api/v1/orders/${orderId}/trailer-tracking`);
}

/**
 * The trailer the order is on and where it is, when this viewer may see it.
 *
 * Not polled, for the same reasons the truck's hook is not: the snapshot is refreshed from the provider when
 * the page loads, and an order open in a tab must not keep spending the carrier's API quota. The server owns
 * the cut-off — a customer whose order is executed is answered with no trailer — so nothing here decides
 * what a viewer may see.
 */
export function useOrderTrailerTracking(orderId: string) {
  return useSWR(['order-trailer-tracking', orderId], () => getOrderTrailerTracking(orderId), {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });
}

export async function getOrderRouteData(orderId: string): Promise<OrderRouteDataResponse> {
  return fetchEndpointData<OrderRouteDataResponse>(`/api/v1/orders/${orderId}/route-data`);
}

export async function calculateOrderRoute(orderId: string): Promise<CalculateOrderRouteResponse> {
  return fetchEndpointData<CalculateOrderRouteResponse>(`/api/v1/orders/${orderId}/calculate-route`, {
    method: 'POST',
    body: {},
  });
}

export async function assignOrder(orderId: string, assigneeId: string | null): Promise<OrderAssignResponse> {
  const body: OrderAssignBody = { assigneeId };
  return fetchEndpointData<OrderAssignResponse>(`/api/v1/orders/${orderId}/assign`, {
    method: 'POST',
    body,
  });
}

// ============================================================================
// Order Chat
// ============================================================================

export type { OrderChatMessage };

export async function getOrderChatMessages(
  orderId: string,
  cursor?: string,
  take?: number,
): Promise<OrderChatMessagesResponse> {
  const params = new URLSearchParams();
  if (cursor) params.set('cursor', cursor);
  if (take) params.set('take', take.toString());

  const queryString = params.toString();
  const url = queryString ? `/api/v1/orders/${orderId}/chat?${queryString}` : `/api/v1/orders/${orderId}/chat`;

  return fetchEndpointData<OrderChatMessagesResponse>(url);
}

export async function sendOrderChatMessage(orderId: string, content: string): Promise<OrderChatSendResponse> {
  return fetchEndpointData<OrderChatSendResponse>(`/api/v1/orders/${orderId}/chat`, {
    method: 'POST',
    body: { content },
  });
}

// ============================================================================
// Order reference data (vat rates, package/cargo/product types)
// ============================================================================

export const CARGO_TYPES_SWR_KEY = 'order-cargo-types';
export const PACKAGE_TYPES_SWR_KEY = 'order-package-types';
export const PRODUCT_TYPES_SWR_KEY = 'order-product-types';
export const VAT_RATES_SWR_KEY = 'order-vat-rates';

// ─── CargoType ───────────────────────────────────────────────────────

export async function getCargoTypes(): Promise<CargoTypesResponse> {
  return fetchEndpointData<CargoTypesResponse>('/api/v1/order-reference-data/cargo-types');
}

// Mirrors `useCargoTypesQuery` in CargoTypeComboboxField — non-suspense, the
// combobox tolerates `data === undefined` and renders an empty list until the
// fetch resolves (exactly as the urql query did).
export function useCargoTypes() {
  return useSWR(CARGO_TYPES_SWR_KEY, getCargoTypes);
}

// Shares the SWR cache key with `useCargoTypes`.
export function useCargoTypesSuspense() {
  return useSWR(CARGO_TYPES_SWR_KEY, getCargoTypes);
}

export async function createCargoType(data: CreateCargoTypeBody): Promise<CargoTypeIdResponse> {
  return fetchEndpointData<CargoTypeIdResponse>('/api/v1/order-reference-data/cargo-types', {
    method: 'POST',
    body: data,
  });
}

export async function updateCargoType(id: string, data: UpdateCargoTypeBody): Promise<CargoTypeIdResponse> {
  return fetchEndpointData<CargoTypeIdResponse>(`/api/v1/order-reference-data/cargo-types/${id}`, {
    method: 'PATCH',
    body: data,
  });
}

// ─── PackageType ─────────────────────────────────────────────────────

export async function getPackageTypes(): Promise<PackageTypesResponse> {
  return fetchEndpointData<PackageTypesResponse>('/api/v1/order-reference-data/package-types');
}

// Mirrors `usePackageTypesQuery` in PackageTypeComboboxField — non-suspense.
export function usePackageTypes() {
  return useSWR(PACKAGE_TYPES_SWR_KEY, getPackageTypes);
}

export function usePackageTypesSuspense() {
  return useSWR(PACKAGE_TYPES_SWR_KEY, getPackageTypes);
}

export async function createPackageType(data: CreatePackageTypeBody): Promise<PackageTypeIdResponse> {
  return fetchEndpointData<PackageTypeIdResponse>('/api/v1/order-reference-data/package-types', {
    method: 'POST',
    body: data,
  });
}

export async function updatePackageType(id: string, data: UpdatePackageTypeBody): Promise<PackageTypeIdResponse> {
  return fetchEndpointData<PackageTypeIdResponse>(`/api/v1/order-reference-data/package-types/${id}`, {
    method: 'PATCH',
    body: data,
  });
}

// ─── ProductType ─────────────────────────────────────────────────────

export async function getProductTypes(): Promise<ProductTypesResponse> {
  return fetchEndpointData<ProductTypesResponse>('/api/v1/order-reference-data/product-types');
}

// Mirrors `useProductTypesQuery` in ProductTypeComboboxField — non-suspense.
export function useProductTypes() {
  return useSWR(PRODUCT_TYPES_SWR_KEY, getProductTypes);
}

export function useProductTypesSuspense() {
  return useSWR(PRODUCT_TYPES_SWR_KEY, getProductTypes);
}

export async function createProductType(data: CreateProductTypeBody): Promise<ProductTypeIdResponse> {
  return fetchEndpointData<ProductTypeIdResponse>('/api/v1/order-reference-data/product-types', {
    method: 'POST',
    body: data,
  });
}

export async function updateProductType(id: string, data: UpdateProductTypeBody): Promise<ProductTypeIdResponse> {
  return fetchEndpointData<ProductTypeIdResponse>(`/api/v1/order-reference-data/product-types/${id}`, {
    method: 'PATCH',
    body: data,
  });
}

// ─── VatRate ─────────────────────────────────────────────────────────

export async function getVatRates(): Promise<VatRatesResponse> {
  return fetchEndpointData<VatRatesResponse>('/api/v1/order-reference-data/vat-rates');
}

// Mirrors `useVatRatesQuery` in VatRateComboboxField — non-suspense, the
// combobox tolerates `data === undefined`.
export function useVatRates() {
  return useSWR(VAT_RATES_SWR_KEY, getVatRates);
}

export function useVatRatesSuspense() {
  return useSWR(VAT_RATES_SWR_KEY, getVatRates);
}

export async function createVatRate(data: CreateVatRateBody): Promise<VatRateIdResponse> {
  return fetchEndpointData<VatRateIdResponse>('/api/v1/order-reference-data/vat-rates', {
    method: 'POST',
    body: data,
  });
}

export async function updateVatRate(id: string, data: UpdateVatRateBody): Promise<VatRateIdResponse> {
  return fetchEndpointData<VatRateIdResponse>(`/api/v1/order-reference-data/vat-rates/${id}`, {
    method: 'PATCH',
    body: data,
  });
}

// ============================================================================
// Order core (tra-93): reads, mutations and SWR hooks.
//
// Conventions (mirroring src/app/sale/models.ts):
//  - read fetchers return the endpoint response; hooks use array SWR keys.
//  - mutation fetchers are plain async functions; callers wrap in try/catch and
//    manage their own isLoading, then call mutate()/refresh() to revalidate.
//  - PDF generation returns IPdfJobRef ({ queue, jobId }) to feed into
//    runPdfJobAndRefresh on the client.
// ============================================================================

export type {
  GetOrdersFilter,
  GetOrdersOrder,
  GetOrdersOrderBy,
  GetOrdersResponse,
  GetOrderByIdResponse,
  IncomingOrderListItem,
  IncomingOrderResponse,
  OrdersByTokenResponse,
  HistoricalOrderLinesResponse,
};

export type OrderDetail = GetOrderByIdResponse;

export const ORDERS_SWR_KEY = 'orders';
export const ORDER_SWR_KEY = 'order';
export const ORDERS_BY_TOKEN_SWR_KEY = 'orders-by-token';
export const INCOMING_ORDER_SWR_KEY = 'incoming-order';

// --- Reads --------------------------------------------------------------

export interface GetOrdersArgs {
  id?: string | null;
  take?: number | null;
  filters?: GetOrdersFilter;
  orderBy?: GetOrdersOrderBy;
}

export async function getOrders(args: GetOrdersArgs): Promise<GetOrdersResponse> {
  const params = new URLSearchParams();
  if (args.id) params.set('id', args.id);
  if (args.take != null) params.set('take', args.take.toString());
  if (args.filters) params.set('filters', JSON.stringify(args.filters));
  if (args.orderBy) params.set('orderBy', JSON.stringify(args.orderBy));
  const queryString = params.toString();
  return fetchEndpointData<GetOrdersResponse>(`/api/v1/orders${queryString ? `?${queryString}` : ''}`);
}

export function useOrders(args: GetOrdersArgs) {
  return useSWR([ORDERS_SWR_KEY, args], () => getOrders(args));
}

export async function getOrderById(orderId: string): Promise<GetOrderByIdResponse> {
  return fetchEndpointData<GetOrderByIdResponse>(`/api/v1/orders/${orderId}`);
}

export function useOrderById(orderId: string | null | undefined) {
  return useSWR(orderId ? [ORDER_SWR_KEY, orderId] : null, () => getOrderById(orderId!));
}

export async function getOrdersByToken(id: string, token: string): Promise<OrdersByTokenResponse> {
  const params = new URLSearchParams({ id, token });
  return fetchEndpointData<OrdersByTokenResponse>(`/api/v1/orders/by-token?${params.toString()}`);
}

export function useOrdersByToken(id: string, token: string) {
  return useSWR([ORDERS_BY_TOKEN_SWR_KEY, id, token], () => getOrdersByToken(id, token));
}

export async function getHistoricalOrderLinesForSuggestions(orderId: string): Promise<HistoricalOrderLinesResponse> {
  return fetchEndpointData<HistoricalOrderLinesResponse>(`/api/v1/orders/${orderId}/historical-line-suggestions`);
}

export async function getIncomingOrders(id?: string, take?: number): Promise<IncomingOrdersResponse> {
  const params = new URLSearchParams();
  if (id) params.set('id', id);
  if (take != null) params.set('take', take.toString());
  const queryString = params.toString();
  return fetchEndpointData<IncomingOrdersResponse>(`/api/v1/incoming-orders${queryString ? `?${queryString}` : ''}`);
}

export async function getIncomingOrder(id: string): Promise<IncomingOrderResponse> {
  return fetchEndpointData<IncomingOrderResponse>(`/api/v1/incoming-orders/${id}`);
}

export function useIncomingOrder(id: string | null | undefined) {
  return useSWR(id ? [INCOMING_ORDER_SWR_KEY, id] : null, () => getIncomingOrder(id!));
}

// --- Order CRUD / status mutations -------------------------------------

export async function createOrder(data: CreateOrderBody): Promise<OrderIdResponse> {
  return fetchEndpointData<OrderIdResponse>('/api/v1/orders', { method: 'POST', body: data });
}

export async function updateOrder(orderId: string, data: UpdateOrderBody): Promise<OrderIdResponse> {
  return fetchEndpointData<OrderIdResponse>(`/api/v1/orders/${orderId}`, { method: 'PATCH', body: data });
}

export async function setOrderSelfBillExclusion(orderId: string, excluded: boolean): Promise<OrderIdResponse> {
  return fetchEndpointData<OrderIdResponse>(`/api/v1/orders/${orderId}/self-bill-exclusion`, {
    method: 'POST',
    body: { excluded } satisfies OrderSelfBillExclusionBody,
  });
}

export async function cancelOrder(orderId: string, reason: string): Promise<OrderIdResponse> {
  return fetchEndpointData<OrderIdResponse>(`/api/v1/orders/${orderId}/cancel`, {
    method: 'POST',
    body: { reason },
  });
}

export async function confirmOrderUsingToken(data: ConfirmOrderByTokenBody): Promise<ConfirmOrderResponse> {
  return fetchEndpointData<ConfirmOrderResponse>('/api/v1/orders/confirm-by-token', { method: 'POST', body: data });
}

export async function updateOrderStatusManually(
  orderId: string,
  data: UpdateOrderStatusBody,
): Promise<OrderIdResponse> {
  return fetchEndpointData<OrderIdResponse>(`/api/v1/orders/${orderId}/status`, { method: 'POST', body: data });
}

export async function changeOrderSaleStatus(
  orderId: string,
  data: ChangeOrderSaleStatusBody,
): Promise<OrderIdResponse> {
  return fetchEndpointData<OrderIdResponse>(`/api/v1/orders/${orderId}/sale-status`, { method: 'POST', body: data });
}

export async function rateOrder(orderId: string, rating: number): Promise<OrderIdResponse> {
  return fetchEndpointData<OrderIdResponse>(`/api/v1/orders/${orderId}/rate`, {
    method: 'POST',
    body: { rating },
  });
}

export async function bulkDuplicateOrder(data: BulkDuplicateOrderBody): Promise<BulkDuplicateOrderResponse> {
  return fetchEndpointData<BulkDuplicateOrderResponse>('/api/v1/orders/bulk-duplicate', {
    method: 'POST',
    body: data,
  });
}

export async function setOrderDocumentValidationStatus(
  orderId: string,
  data: SetOrderDocumentValidationStatusBody,
): Promise<SetOrderDocumentValidationStatusResponse> {
  return fetchEndpointData<SetOrderDocumentValidationStatusResponse>(
    `/api/v1/orders/${orderId}/document-validation-status`,
    { method: 'POST', body: data },
  );
}

// --- Order line mutations ----------------------------------------------

export async function createOrderLine(orderId: string, data: CreateOrderLineBody): Promise<NumericIdResponse> {
  return fetchEndpointData<NumericIdResponse>(`/api/v1/orders/${orderId}/lines`, { method: 'POST', body: data });
}

export async function createOrderLineStop(
  orderLineId: number,
  data: CreateOrderLineStopBody,
): Promise<StringIdResponse> {
  return fetchEndpointData<StringIdResponse>(`/api/v1/order-lines/${orderLineId}/stops`, {
    method: 'POST',
    body: data,
  });
}

export async function updateOrderLineStop(id: number, data: UpdateOrderLineStopBody): Promise<StringIdResponse> {
  return fetchEndpointData<StringIdResponse>(`/api/v1/order-line-stops/${id}`, { method: 'PATCH', body: data });
}

export async function deleteOrderLineStop(id: number): Promise<StringIdResponse> {
  return fetchEndpointData<StringIdResponse>(`/api/v1/order-line-stops/${id}`, { method: 'DELETE' });
}

export async function moveOrderLineStop(id: number, offset: number): Promise<StringIdResponse> {
  return fetchEndpointData<StringIdResponse>(`/api/v1/order-line-stops/${id}/move`, {
    method: 'POST',
    body: { offset },
  });
}

export async function createOrderLineContent(
  orderLineId: number,
  data: CreateOrderLineContentBody,
): Promise<NumericIdResponse> {
  return fetchEndpointData<NumericIdResponse>(`/api/v1/order-lines/${orderLineId}/contents`, {
    method: 'POST',
    body: data,
  });
}

export async function updateOrderLineContent(id: number, data: UpdateOrderLineContentBody): Promise<NumericIdResponse> {
  return fetchEndpointData<NumericIdResponse>(`/api/v1/order-line-contents/${id}`, { method: 'PATCH', body: data });
}

export async function deleteOrderLineContent(id: number): Promise<NumericIdResponse> {
  return fetchEndpointData<NumericIdResponse>(`/api/v1/order-line-contents/${id}`, { method: 'DELETE' });
}

export async function createOrderLinePurchase(
  orderLineId: number,
  data: CreateOrderLinePurchaseBody,
): Promise<NumericIdResponse> {
  return fetchEndpointData<NumericIdResponse>(`/api/v1/order-lines/${orderLineId}/purchases`, {
    method: 'POST',
    body: data,
  });
}

export async function updateOrderLinePurchase(
  id: number,
  data: UpdateOrderLinePurchaseBody,
): Promise<NumericIdResponse> {
  return fetchEndpointData<NumericIdResponse>(`/api/v1/order-line-purchases/${id}`, { method: 'PATCH', body: data });
}

export async function deleteOrderLinePurchase(id: number): Promise<NumericIdResponse> {
  return fetchEndpointData<NumericIdResponse>(`/api/v1/order-line-purchases/${id}`, { method: 'DELETE' });
}

export async function createOrderLineSale(
  orderLineId: number,
  data: CreateOrderLineSaleBody,
): Promise<NumericIdResponse> {
  return fetchEndpointData<NumericIdResponse>(`/api/v1/order-lines/${orderLineId}/sales`, {
    method: 'POST',
    body: data,
  });
}

export async function updateOrderLineSale(id: number, data: UpdateOrderLineSaleBody): Promise<NumericIdResponse> {
  return fetchEndpointData<NumericIdResponse>(`/api/v1/order-line-sales/${id}`, { method: 'PATCH', body: data });
}

export async function deleteOrderLineSale(id: number): Promise<NumericIdResponse> {
  return fetchEndpointData<NumericIdResponse>(`/api/v1/order-line-sales/${id}`, { method: 'DELETE' });
}

// --- Documents + complaints --------------------------------------------

export async function linkOrderDocument(orderId: string, data: LinkOrderDocumentBody): Promise<StringIdResponse> {
  return fetchEndpointData<StringIdResponse>(`/api/v1/orders/${orderId}/documents/link`, {
    method: 'POST',
    body: data,
  });
}

export async function unlinkOrderDocument(orderId: string, data: UnlinkOrderDocumentBody): Promise<StringIdResponse> {
  return fetchEndpointData<StringIdResponse>(`/api/v1/orders/${orderId}/documents/unlink`, {
    method: 'POST',
    body: data,
  });
}

export async function internalLogOrderComplaint(
  orderId: string,
  data: InternalLogOrderComplaintBody,
): Promise<StringIdResponse> {
  return fetchEndpointData<StringIdResponse>(`/api/v1/orders/${orderId}/complaints`, { method: 'POST', body: data });
}

export async function updateOrderComplaint(
  complaintId: string,
  data: UpdateOrderComplaintBody,
): Promise<UpdateOrderComplaintResponse> {
  return fetchEndpointData<UpdateOrderComplaintResponse>(`/api/v1/order-complaints/${complaintId}`, {
    method: 'PATCH',
    body: data,
  });
}

export async function deleteOrderComplaint(complaintId: string): Promise<StringIdResponse> {
  return fetchEndpointData<StringIdResponse>(`/api/v1/order-complaints/${complaintId}`, { method: 'DELETE' });
}

export async function linkOrderComplaintDocument(
  complaintId: string,
  documentId: string,
): Promise<ComplaintAttachmentsResponse> {
  return fetchEndpointData<ComplaintAttachmentsResponse>(`/api/v1/order-complaints/${complaintId}/documents/link`, {
    method: 'POST',
    body: { documentId },
  });
}

export async function unlinkOrderComplaintDocument(
  complaintId: string,
  documentId: string,
): Promise<ComplaintAttachmentsResponse> {
  return fetchEndpointData<ComplaintAttachmentsResponse>(`/api/v1/order-complaints/${complaintId}/documents/unlink`, {
    method: 'POST',
    body: { documentId },
  });
}

export async function generateSustainabilityReport(orderId: string): Promise<IPdfJobRef> {
  return fetchEndpointData<IPdfJobRef>(`/api/v1/orders/${orderId}/sustainability-report`, {
    method: 'POST',
    body: {},
  });
}

export async function generateOrderConfirmation(orderId: string): Promise<IPdfJobRef> {
  return fetchEndpointData<IPdfJobRef>(`/api/v1/orders/${orderId}/order-confirmation-pdf`, {
    method: 'POST',
    body: {},
  });
}

export async function sendOrderConfirmation(
  orderId: string,
  data: SendOrderConfirmationBody,
): Promise<StringIdResponse> {
  return fetchEndpointData<StringIdResponse>(`/api/v1/orders/${orderId}/send-order-confirmation`, {
    method: 'POST',
    body: data,
  });
}

export async function sendTransportOrder(orderId: string, data: SendTransportOrderBody): Promise<StringIdResponse> {
  return fetchEndpointData<StringIdResponse>(`/api/v1/orders/${orderId}/send-transport-order`, {
    method: 'POST',
    body: data,
  });
}

export async function sendUrgentCmrReminder(
  orderId: string,
  data: SendUrgentCmrReminderBody,
): Promise<StringIdResponse> {
  return fetchEndpointData<StringIdResponse>(`/api/v1/orders/${orderId}/send-urgent-cmr-reminder`, {
    method: 'POST',
    body: data,
  });
}
